
import React from 'react';
import { Link } from '@/components/LocaleLink';
import { useLanguage } from '@/contexts/language/LanguageContext';
import SEO from '@/components/SEO';
import Navigation from '@/components/Navigation';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import useScrollReveal from '@/hooks/useScrollReveal';
import PodcastSection from '@/components/home/PodcastSection';

// Display order on the page. Role and quote come from about.ts (en + sk).
const team = [
  {
    id: 'mfa',
    name: 'Martin Faith',
    roleKey: 'teamMfaRole',
    lineKey: 'teamMfaLine',
    photo: '/team/mfa.png',
  },
  {
    id: 'jka',
    name: 'Juraj Kapasný',
    roleKey: 'teamJkaRole',
    lineKey: 'teamJkaLine',
    photo: '/team/jka.png',
  },
  {
    id: 'eha',
    name: 'Eva Pavla Halušková',
    roleKey: 'teamEhaRole',
    lineKey: 'teamEhaLine',
    photo: '/team/eha.png',
  },
  {
    id: 'mko',
    name: 'Martin Kováčik',
    roleKey: 'teamMkoRole',
    lineKey: 'teamMkoLine',
    photo: '/team/mko.png',
  },
  {
    id: 'kvr',
    name: 'Kristína Vršanská',
    roleKey: 'teamKvrRole',
    lineKey: 'teamKvrLine',
    photo: '/team/kvr.webp',
  },
  {
    id: 'mo',
    name: 'Matej Ondica',
    roleKey: 'teamMoRole',
    lineKey: 'teamMoLine',
    photo: '/team/mo.png',
  },
  {
    id: 'dst',
    name: 'Denis Štrbák',
    roleKey: 'teamDstRole',
    lineKey: 'teamDstLine',
    photo: '/team/dst.png',
  },
  {
    id: 'pk',
    name: 'Petra Kantoríková',
    roleKey: 'teamPkRole',
    lineKey: 'teamPkLine',
    photo: '/team/pk.png',
  },
];

const About = () => {
  const { t } = useLanguage();
  
  // Add scroll reveal effect
  useScrollReveal();

  return (
    <div className="flex flex-col min-h-screen">
      <SEO
        title={t('aboutSeoTitle')}
        description={t('aboutSeoDescription')}
        canonical="/about"
      />
      <Navigation />
      
      {/* Hero Section */}
      <section className="pt-24 pb-16 bg-background relative overflow-hidden">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-primary/10 rounded-full filter blur-3xl"></div>
        
        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center reveal">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
              {t('aboutTitle') || "About Us"}
            </h1>
            <p className="text-xl md:text-2xl font-medium text-muted-foreground">
              {t('aboutTagline') || "Being data driven is not about having reports. You are data driven when your data works for you!"}
            </p>
          </div>
        </div>
      </section>
      
      {/* Main Content */}
      <section className="py-16 bg-secondary/10">
        <div className="container-custom">
          <div className="reveal">
            <div>
              <h2 className="text-3xl font-semibold mb-6">{t('ourMission')}</h2>
              <p className="text-xl md:text-2xl font-medium text-foreground">
                {t('aboutMissionLead')}
              </p>
              <p className="font-semibold text-foreground mt-8 mb-3">
                {t('aboutFoundationTitle')}
              </p>
              <p className="text-muted-foreground">
                {t('aboutFoundationText')}
              </p>
              <p className="text-muted-foreground mt-6">
                {t('aboutPathsIntro')}
              </p>
              <div className="grid md:grid-cols-2 gap-6 mt-6">
                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold mb-2">{t('aboutPathBuildTitle')}</h3>
                    <p className="text-muted-foreground">{t('aboutPathBuildDesc')}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="p-6">
                    <h3 className="text-lg font-semibold mb-2">{t('aboutPathRasTitle')}</h3>
                    <p className="text-muted-foreground">{t('aboutPathRasDesc')}</p>
                  </CardContent>
                </Card>
              </div>
              <p className="text-muted-foreground mt-6">
                <span className="font-semibold text-foreground">{t('aboutStartTitle')}</span>{' '}
                {t('aboutStartText')}
              </p>
              <p className="text-3xl font-semibold mt-10">
                <span className="text-foreground">{t('aboutPutYour')}</span>
                <span className="text-accent">{t('aboutDataToWork')}</span>
              </p>
            </div>
          </div>
        </div>
      </section>
      
      {/* Team Section */}
      <section className="py-16 bg-background">
        <div className="container-custom">
          <div className="text-center mb-12 reveal">
            <h2 className="text-3xl font-semibold mb-4">{t('ourTeam')}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t('teamIntro')}
            </p>
          </div>

          <div className="max-w-5xl mx-auto flex flex-wrap justify-center gap-x-8 gap-y-12 reveal">
            {team.map((member) => (
              <div key={member.id} className="w-full sm:w-56 text-center">
                <img
                  src={member.photo}
                  alt={member.name || t(member.roleKey)}
                  loading="lazy"
                  width={400}
                  height={400}
                  className="w-44 h-auto mx-auto"
                />
                {member.name && <h3 className="text-lg font-semibold mt-2">{member.name}</h3>}
                {member.roleKey && (
                  <p className="text-sm font-medium text-foreground/70">{t(member.roleKey)}</p>
                )}
                {member.lineKey && (
                  <p className="text-sm text-muted-foreground mt-3">{t(member.lineKey)}</p>
                )}
              </div>
            ))}
          </div>

          <div className="text-center mt-16 reveal">
            <Link
              to="/tell-me-more"
              className="inline-flex items-center gap-2 px-8 py-3.5 rounded-lg bg-[#FF6B35] text-white font-semibold text-lg shadow-md hover:bg-[#FF6B35]/90 hover:shadow-lg transition-all"
            >
              {t('missionCtaText')}
            </Link>
            <p className="text-sm text-muted-foreground/70 mt-3">
              {t('missionCtaDescription')}
            </p>
          </div>
        </div>
      </section>

      {/* Principles Section */}
      <section className="py-16 bg-secondary/10">
        <div className="container-custom">
          <div className="text-center mb-12 reveal">
            <h2 className="text-3xl font-semibold mb-4">{t('ourPrinciples')}</h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              {t('principlesIntro')}
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 reveal">
            {/* Principle 1 */}
            <Card className="bg-white/80 backdrop-blur-md border-primary/10 hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                  <span className="text-xl font-bold text-primary">1</span>
                </div>
                <h3 className="text-xl font-semibold mb-2">{t('principle1Title')}</h3>
                <p className="text-muted-foreground">
                  {t('principle1Desc')}
                </p>
              </CardContent>
            </Card>
            
            {/* Principle 2 */}
            <Card className="bg-white/80 backdrop-blur-md border-primary/10 hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                  <span className="text-xl font-bold text-primary">2</span>
                </div>
                <h3 className="text-xl font-semibold mb-2">{t('principle2Title')}</h3>
                <p className="text-muted-foreground">
                  {t('principle2Desc')}
                </p>
              </CardContent>
            </Card>
            
            {/* Principle 3 */}
            <Card className="bg-white/80 backdrop-blur-md border-primary/10 hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                  <span className="text-xl font-bold text-primary">3</span>
                </div>
                <h3 className="text-xl font-semibold mb-2">{t('principle3Title')}</h3>
                <p className="text-muted-foreground">
                  {t('principle3Desc')}
                </p>
              </CardContent>
            </Card>
            
            {/* Principle 4 */}
            <Card className="bg-white/80 backdrop-blur-md border-primary/10 hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                  <span className="text-xl font-bold text-primary">4</span>
                </div>
                <h3 className="text-xl font-semibold mb-2">{t('principle4Title')}</h3>
                <p className="text-muted-foreground">
                  {t('principle4Desc')}
                </p>
              </CardContent>
            </Card>
            
            {/* Principle 5 */}
            <Card className="bg-white/80 backdrop-blur-md border-primary/10 hover:shadow-md transition-shadow">
              <CardContent className="pt-6">
                <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mb-4">
                  <span className="text-xl font-bold text-primary">5</span>
                </div>
                <h3 className="text-xl font-semibold mb-2">{t('principle5Title')}</h3>
                <p className="text-muted-foreground">
                  {t('principle5Desc')}
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Podcasts (SK only) */}
      <PodcastSection className="container-custom py-16 reveal" />

      {/* CTA Section */}
      <section className="py-16 bg-secondary/20">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center reveal">
            <h2 className="text-3xl font-semibold mb-6">{t('readyToWork')}</h2>
            <Link
              to="/book-a-call"
              className="btn-primary inline-flex items-center px-6 py-3 rounded-md bg-primary text-primary-foreground shadow hover:bg-primary/90 transition-colors"
            >
              {t('contactUsToday')}
            </Link>
            <p className="text-sm text-muted-foreground/70 mt-3 max-w-xl mx-auto">
              {t('ctaDescription')}
            </p>
          </div>
        </div>
      </section>
      
      <Footer />
    </div>
  );
};

export default About;
